const DEFAULT_IDLE_MS = 120;
const DEFAULT_COOLDOWN_MS = 220;
const LINE_HEIGHT_PX = 40; // approximate pixel-equivalent of one DOM_DELTA_LINE tick

class Wheel {
  /**
   * @param {Number} threshold define in percents how much accumulated horizontal delta is
   *   needed to trigger a page, relative to the window width, used in `underThreshold`.
   * @param {Number} [idleMs] gap in milliseconds with no wheel events after which the
   *   accumulated delta resets, so a gentle/abandoned scroll doesn't linger and page later.
   * @param {Number} [cooldownMs] milliseconds to lock out further accumulation after `lock` is
   *   called, so a single gesture pages once instead of repeatedly while wheel events keep firing.
   */
  constructor(threshold, { idleMs = DEFAULT_IDLE_MS, cooldownMs = DEFAULT_COOLDOWN_MS } = {}) {
    this.threshold = threshold;
    this.idleMs = idleMs;
    this.cooldownMs = cooldownMs;
    this.#setDefaults();
  }

  /**
   * @param {Number} width current width of a window
   * @return {boolean} whether or not a page change should be triggered
   */
  underThreshold(width) {
    return (100 / width) * Math.abs(this.accumulated) < this.threshold;
  }

  /**
   * Tracks a wheel event and reports which edge it's moving towards. Horizontal events
   * ('left'/'right') accumulate delta, used by `underThreshold`, unless locked (see `lock`);
   * vertical events ('top'/'bottom') never accumulate, so the caller can leave normal page
   * scroll alone.
   *
   * @param {WheelEvent} e
   * @param {Number} width current width of a window, used to normalize `DOM_DELTA_PAGE` deltas
   * @return {('left'|'right'|'top'|'bottom')}
   */
  moved(e, width) {
    let { deltaX, deltaY } = e;

    // deltaX/deltaY aren't always pixels: DOM_DELTA_LINE (a common default for a traditional
    // mouse wheel, e.g. on Firefox/Windows) reports small tick counts, and DOM_DELTA_PAGE
    // reports whole-viewport counts. Both need normalizing to pixels before accumulating,
    // since `underThreshold` compares the accumulated delta against a pixel width.
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      deltaX *= LINE_HEIGHT_PX;
      deltaY *= LINE_HEIGHT_PX;
    } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      deltaX *= width;
      deltaY *= width;
    }

    if (e.shiftKey) {
      deltaX = deltaX !== 0 ? deltaX : deltaY;
      deltaY = 0;
    }

    if (Math.abs(deltaX) <= Math.abs(deltaY)) {
      return deltaY > 0 ? 'bottom' : 'top';
    }

    if (!this.locked) {
      clearTimeout(this.idleTimer);
      this.accumulated += deltaX;
      this.idleTimer = setTimeout(() => (this.accumulated = 0), this.idleMs);
    }

    return this.accumulated > 0 ? 'right' : 'left';
  }

  /**
   * Locks out further accumulation for `cooldownMs`, so a page change triggered off the back of
   * `underThreshold` isn't immediately re-triggered by the rest of the same physical gesture.
   */
  lock() {
    this.accumulated = 0;
    this.locked = true;
    clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => (this.locked = false), this.cooldownMs);
  }

  reset() {
    clearTimeout(this.idleTimer);
    clearTimeout(this.lockTimer);
    this.#setDefaults();
  }

  #setDefaults() {
    this.accumulated = 0;
    this.locked = false;
  }
}

export default Wheel;
